import { AssetGroup } from "../../model/groups/SDKAssetGroup";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Function Definition
export class AssetGroupResolver {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(assets) {
        this.assets = assets;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Resolution
    resolveGroupData(data) {
        const hashedGroups = new Map();
        const resolvedGroups = new Map();
        const allowedAssetIds = this.assets.map((a) => a.id);
        const assetIdsToSourceId = new Map();
        this.assets.forEach((asset) => {
            var _a;
            if ((_a = asset.origin) === null || _a === void 0 ? void 0 : _a.sourceId) {
                assetIdsToSourceId.set(asset.id, asset.origin.sourceId);
            }
        });
        const assetIdsToSourceName = new Map();
        this.assets.forEach((asset) => {
            var _a;
            if ((_a = asset.origin) === null || _a === void 0 ? void 0 : _a.fileName) {
                assetIdsToSourceName.set(asset.id, asset.origin.fileName);
            }
        });
        // Convert raw groups to resolved groups, not caring about the references just yet
        data.forEach((rawGroup) => {
            const group = new AssetGroup(rawGroup);
            hashedGroups.set(rawGroup.persistentId, rawGroup);
            resolvedGroups.set(rawGroup.persistentId, group);
        });
        // Build the reference tree and list of assets
        data.forEach((rawGroup) => {
            const filteredAssetIds = new Array();
            const referencedGroup = resolvedGroups.get(rawGroup.persistentId);
            rawGroup.childrenIds.forEach((id) => {
                // Find if reference is group - if it is not, it is a asset or designComponent
                const childGroup = resolvedGroups.get(id);
                if (childGroup) {
                    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                    referencedGroup.addChild(childGroup.persistentId);
                }
                else if (allowedAssetIds.includes(id)) {
                    // Only allow assets, and filter out designComponents
                    filteredAssetIds.push(id);
                }
            });
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            referencedGroup.assetIds = filteredAssetIds;
        });
        // Retrieve resolved groups
        const groups = Array.from(resolvedGroups.values());
        this.recomputePaths(groups);
        this.recomputeParents(groups);
        const orderedGroups = this.reorderGroupsByRoots(groups);
        // Mark asset groups
        this.markAssetGroups(orderedGroups);
        this.assignSourceDataToGroups(groups, assetIdsToSourceId, assetIdsToSourceName);
        const assetGroups = orderedGroups.filter((group) => group.isAssetGroup);
        return assetGroups;
    }
    // Mark groups that directly contain assets and their ancestors
    markAssetGroups(groups) {
        groups.forEach((group) => {
            if (group.assetIds.length > 0) {
                group.isAssetGroup = true;
                if (group.parentGroupId) {
                    this.markAssetAncestors(group.parentGroupId, groups);
                }
            }
        });
    }
    // Helper function to recursively mark ancestor groups
    markAssetAncestors(parentGroupId, allGroups) {
        const parentGroup = allGroups.find((g) => g.persistentId === parentGroupId);
        if (parentGroup) {
            parentGroup.isAssetGroup = true;
            if (parentGroup.parentGroupId) {
                this.markAssetAncestors(parentGroup.parentGroupId, allGroups);
            }
        }
    }
    assignSourceDataToGroups(groups, assetIdsToSourceId, assetIdsToSourceName) {
        // Assign sources to groups containing assets
        groups.forEach((group) => {
            const sources = new Map();
            group.assetIds.forEach((assetId) => {
                const sourceId = assetIdsToSourceId.get(assetId);
                const sourceName = assetIdsToSourceName.get(assetId);
                if (sourceId && sourceName) {
                    sources.set(sourceId, { id: sourceId, name: sourceName });
                    // Set first source as the default sourceId/sourceName for backwards compatibility
                    if (!group.sourceId) {
                        group.sourceId = sourceId;
                        group.sourceName = sourceName;
                    }
                }
            });
            // Convert map to array and assign to group
            const sourcesArray = Array.from(sources.values());
            if (sourcesArray.length > 0) {
                const actualSourceIds = new Set(group.sources.map((s) => s.id));
                const newSources = sourcesArray.filter((s) => !actualSourceIds.has(s.id));
                group.sources.push(...newSources);
            }
            // spread it recursively to all ancestors
            if (group.parentGroupId && group.sourceId && group.sourceName) {
                this.propagateSourceDataToAncestors(group.parentGroupId, group.sources, groups);
            }
        });
    }
    propagateSourceDataToAncestors(parentGroupId, sources, allGroups) {
        var _a, _b;
        const parentGroup = allGroups.find((g) => g.persistentId === parentGroupId);
        if (parentGroup) {
            // Create a map of existing sources to avoid duplicates
            const existingSources = new Map((_b = (_a = parentGroup.sources) === null || _a === void 0 ? void 0 : _a.map((s) => [s.id, s])) !== null && _b !== void 0 ? _b : []);
            // Add new sources that don't already exist
            sources.forEach((source) => {
                if (!existingSources.has(source.id)) {
                    if (!parentGroup.sources) {
                        parentGroup.sources = [];
                    }
                    parentGroup.sources.push(source);
                    // Set first source as the default sourceId/sourceName for backwards compatibility
                    if (!parentGroup.sourceId) {
                        parentGroup.sourceId = source.id;
                        parentGroup.sourceName = source.name;
                    }
                }
            });
            if (parentGroup.parentGroupId) {
                this.propagateSourceDataToAncestors(parentGroup.parentGroupId, sources, allGroups);
            }
        }
    }
    recomputePaths(groups) {
        // Find roots, and compute the segments down from the roots
        groups.forEach((group) => {
            if (group.isRoot) {
                this.recomputePathsFromRoot(group, [], groups);
            }
        });
        // Drop first item because we don't want the core root category to be there
        groups.forEach((group) => {
            group.path.shift();
        });
    }
    recomputePathsFromRoot(root, segments, groups) {
        // Recursively go down the tree(s) and add segments to each
        const extendedPath = segments.concat(root.name);
        root.subgroupIds.forEach((groupId) => {
            const group = groups.find((g) => g.persistentId === groupId);
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            group.path = extendedPath.concat();
            // @ts-expect-error TS(2345): Argument of type 'AssetGroup | undefined' is not a... Remove this comment to see the full error message
            this.recomputePathsFromRoot(group, extendedPath, groups);
        });
    }
    reorderGroupsByRoots(groups) {
        let sortedGroups = new Array();
        // Find the root groups, which will be initial sorting points
        const roots = groups.filter((g) => g.isRoot);
        roots.forEach((root) => {
            // For each group, traverse and add proper order
            sortedGroups = sortedGroups.concat(this.traverseSortGroup(root, groups));
        });
        return sortedGroups;
    }
    traverseSortGroup(group, groups) {
        let output = new Array();
        // Iterated group always first
        output.push(group);
        // Add sorted groups to the array
        const sortedGroupIds = group.subgroupIds.sort((g1, g2) => group.childrenIds.indexOf(g1) - group.childrenIds.indexOf(g2));
        sortedGroupIds.forEach((subgroupId) => {
            const subgroup = groups.find((g) => g.persistentId === subgroupId);
            // @ts-expect-error TS(2345): Argument of type 'AssetGroup | undefined' is not a... Remove this comment to see the full error message
            output = output.concat(this.traverseSortGroup(subgroup, groups));
        });
        return output;
    }
    recomputeParents(groups) {
        // Find roots, and compute the references down the chain. Root groups don't have parents
        groups.forEach((group) => {
            if (group.isRoot) {
                this.recomputeParentsFromRoot(group, groups);
                group.parentGroupId = null;
            }
        });
    }
    recomputeParentsFromRoot(rootGroup, groups) {
        rootGroup.subgroupIds.forEach((subgroupId) => {
            const subgroup = groups.find((g) => g.persistentId === subgroupId);
            // @ts-expect-error TS(2532): Object is possibly 'undefined'.
            subgroup.setParentGroupId(rootGroup.persistentId);
            // @ts-expect-error TS(2345): Argument of type 'AssetGroup | undefined' is not a... Remove this comment to see the full error message
            this.recomputeParentsFromRoot(subgroup, groups);
        });
    }
}
